import { ContentState, convertFromRaw, EditorState } from 'draft-js';
import { defaultItemValues, ItemTypes, viewerTypes } from '../../constants/constants';
import { decorator } from '../RichEditor/utils';

// Boxes factices de l'aperçu des paramètres. Jamais dans le store : ni
// sauvegardées, ni diffusées en collab, ni lues par le sommaire ou le PDF.
// Préfixe d'id propre pour ne jamais croiser une box du document.
const PREVIEW_ID_PREFIX = '__preview-';

export const previewTitleBox = (key, type, indexTitle, text) => ({
  id: `${PREVIEW_ID_PREFIX}${key}`,
  type,
  content: {
    indexTitle,
    editorState: EditorState.createWithContent(ContentState.createFromText(text)),
  },
});

// Trois lignes par titre : sur une seule, l'alignement vertical du numéro
// (haut / centre / bas) ne se verrait pas.
const titleText = (level) => `Style du titre de niveau ${level}\nDeuxième ligne\nTroisième ligne`;

export const previewTitleBoxes = [
  previewTitleBox('title-1', 'title1', [1], titleText(1)),
  previewTitleBox('title-2', 'title2', [1, 1], titleText(2)),
  previewTitleBox('title-3', 'title3', [1, 1, 1], titleText(3)),
  previewTitleBox('title-4', 'title4', [1, 1, 1, 1], titleText(4)),
];

// Réglages par défaut d'un type de box, ceux que prend une box qu'on pose dans
// le document. Repli sur les valeurs de base pour un document qui n'en a pas
// encore enregistré pour ce type.
const baseBox = (gridConfiguration, type, key) => ({
  ...(gridConfiguration[type] ?? defaultItemValues[type]),
  id: `${PREVIEW_ID_PREFIX}${key}`,
  type,
  fromViewer: viewerTypes.PREVIEW,
  isBoxSimulated: true,
});

export const previewEditorState = (text) =>
  EditorState.createWithContent(ContentState.createFromText(text), decorator);

// Texte d'aperçu, dit comme tel : un faux latin laisserait croire que la page
// montre du contenu du document.
const PARAGRAPH =
  "Texte d'aperçu : ce texte ne se trouve pas dans votre document, il sert " +
  "seulement à visualiser l'effet des réglages. Modifiez un paramètre et la " +
  'page se met à jour ici, en direct.';

const rawBlock = (key, text, type = 'unstyled', depth = 0) => ({
  key,
  text,
  type,
  depth,
  inlineStyleRanges: [],
  entityRanges: [],
  data: {},
});

// Assez de texte pour remplir le bas de page, quelle que soit la hauteur de la
// zone de saut de page (onglet Document).
export const previewLongText = previewEditorState(Array(12).fill(PARAGRAPH).join(' '));

export const previewParagraph = EditorState.createWithContent(
  ContentState.createFromText(PARAGRAPH),
  decorator
);

// Un paragraphe puis une puce par niveau de liste (onglet Police : symboles et
// couleurs des puces).
export const previewParagraphAndList = EditorState.createWithContent(
  convertFromRaw({
    entityMap: {},
    blocks: [
      rawBlock('paragraph', PARAGRAPH),
      ...[0, 1, 2, 3, 4].map((depth) =>
        rawBlock(`bullet-${depth}`, `Puce de niveau ${depth + 1}`, 'unordered-list-item', depth)
      ),
    ],
  }),
  decorator
);

// Style « Défaut » par défaut : police, taille et couleur des paramètres du
// document (cf. RichEditor).
export const previewTextBox = (gridConfiguration, key, editorState, width, textStyleId = 0) => {
  const box = baseBox(gridConfiguration, ItemTypes.TEXT, key);
  return {
    ...box,
    textStyleId,
    width,
    content: { ...box.content, editorState },
  };
};

export const previewImageBox = (gridConfiguration, key) =>
  baseBox(gridConfiguration, ItemTypes.IMG, key);

export const previewShapeBox = (gridConfiguration, key) =>
  baseBox(gridConfiguration, ItemTypes.SHAPE, key);

const TABLE_COLUMNS = [
  { key: 'designation', title: 'Désignation', share: 0.5 },
  { key: 'quantity', title: 'Quantité', share: 0.2 },
  { key: 'price', title: 'Prix', share: 0.3 },
];

// Assez de lignes pour voir l'alternance des lignes paires et impaires.
const TABLE_ROWS = [
  ['Étude préalable', '1', '1 200 €'],
  ['Installation', '2', '3 400 €'],
  ['Maintenance annuelle', '1', '850 €'],
  ['Formation', '3', '600 €'],
];

// Tableau custom (pas de tableName) : rien à aller chercher côté serveur. Les
// largeurs de colonnes sont posées, le composant n'a donc pas à les calculer
// (il écrirait alors dans le store).
export const previewTableBox = (gridConfiguration, tableStyleId, width) => {
  const box = baseBox(gridConfiguration, ItemTypes.TABLE, 'table');
  return {
    ...box,
    width,
    tableStyleId,
    content: {
      columns: TABLE_COLUMNS.map(({ key, title, share }) => ({
        key,
        title,
        width: Math.floor(width * share),
      })),
      columnsDisposition: [],
      data: TABLE_ROWS.map((cells, rowIndex) => ({
        key: `row-${rowIndex}`,
        hiddenColumnsKeys: [],
        ...Object.fromEntries(TABLE_COLUMNS.map(({ key }, colIndex) => [key, cells[colIndex]])),
      })),
    },
  };
};

// Titres factices du sommaire : deux chapitres qui passent par les quatre
// niveaux, avec de faux numéros de page (colonne de droite).
export const previewSummaryTitles = [
  ['title1', [1], 'Présentation', 3],
  ['title2', [1, 1], 'Notre entreprise', 3],
  ['title3', [1, 1, 1], 'Nos références', 4],
  ['title4', [1, 1, 1, 1], 'Secteur industriel', 4],
  ['title2', [1, 2], 'Notre équipe', 5],
  ['title1', [2], 'Proposition technique', 7],
  ['title2', [2, 1], 'Méthodologie', 7],
  ['title3', [2, 1, 1], 'Planning', 9],
].map(([type, indexTitle, text, pageNumber], index) => ({
  id: `${PREVIEW_ID_PREFIX}summary-${index}`,
  type,
  indexTitle,
  text,
  pageNumber,
}));

// Cases à cocher : deux réponses sur quatre cochées, pour voir les deux états
// de la case. Disposition du style (pas de disposition propre à la box).
export const previewCheckboxBox = (gridConfiguration, key, checkboxStyleId, width) => ({
  ...baseBox(gridConfiguration, ItemTypes.CHECKBOXS_VARIABLE, key),
  width,
  checkboxStyleId,
  disposition: undefined,
  maxNumberOfAnwser: 4,
  checkboxs: [
    ['Installation sur site', true],
    ['Formation des équipes', false],
    ['Maintenance annuelle', true],
    ['Assistance téléphonique', false],
  ].map(([label, checked]) => ({ label: previewEditorState(label), checked })),
});

// Choix : réponses déjà sélectionnées (le texte « Sélectionnez une réponse »
// ne s'affiche pas en aperçu). Plusieurs réponses montrent le séparateur.
export const previewSelectorBox = (gridConfiguration, key, selectorStyleId, width, selectedLabels) => ({
  ...baseBox(gridConfiguration, ItemTypes.SELECTOR_VARIABLE, key),
  width,
  selectorStyleId,
  isSelected: false,
  maxNumberOfAnwser: selectedLabels.length,
  options: selectedLabels.map((label, index) => ({
    id: `${PREVIEW_ID_PREFIX}${key}-option-${index}`,
    label: previewEditorState(label),
    selected: true,
  })),
});
